import { useState, useEffect, useCallback } from "react";
import profileImg from "../assets/images/profile_giblli.webp";
import "../styles/sidebar.css";
import resumePdf from "../assets/docs/Sailesh_Kafle_Resume.pdf";

import { ReactComponent as GitHubIcon } from "../assets/icons/github.svg";
import { ReactComponent as LinkedInIcon } from "../assets/icons/linkedin.svg";

const Sidebar = ({ mainContentRef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Restore the saved preference, falling back to the OS-level setting.
    const saved = localStorage.getItem("theme");
    if (saved === "dark" || saved === "light") {
      return saved === "dark";
    }
    return (
      window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches
    );
  });
  const [activeSection, setActiveSection] = useState("home");
  const [pendingScrollId, setPendingScrollId] = useState(null);
  const [scrollYPosition, setScrollYPosition] = useState(0);

  const isMobileOrTablet = useCallback(() => {
    return window.innerWidth <= 1023; // Matches your media query breakpoint
  }, []);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", isDarkMode);
    localStorage.setItem("theme", isDarkMode ? "dark" : "light");
  }, [isDarkMode]);

  // Scrollspy: highlight the nav link for whichever section is currently in view.
  useEffect(() => {
    const sectionIds = ["home", "skills", "projects", "blog-publications"];
    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter(Boolean);

    if (sections.length === 0) return;

    let observer;

    const setupObserver = () => {
      if (observer) observer.disconnect();
      // Desktop scrolls the main-content div; mobile/tablet scrolls the window.
      const root = isMobileOrTablet() ? null : mainContentRef.current;
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              setActiveSection(entry.target.id);
            }
          });
        },
        // A band near the top of the viewport marks the "active" section. Keeping it
        // high (rather than centered) ensures the short hero still wins when scrolled
        // to the top — a centered band would land on Skills instead.
        { root, rootMargin: "-15% 0px -80% 0px", threshold: 0 },
      );
      sections.forEach((section) => observer.observe(section));
    };

    setupObserver();
    window.addEventListener("resize", setupObserver);

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener("resize", setupObserver);
    };
  }, [mainContentRef, isMobileOrTablet]);

  useEffect(() => {
    if (isMobileOrTablet()) {
      if (isOpen) {
        setScrollYPosition(window.scrollY);
        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.top = `-${window.scrollY}px`;
        document.body.style.width = "100%";
      } else {
        const unlockDelay = 200;

        const timer = setTimeout(() => {
          document.body.style.overflow = "";
          document.body.style.position = "";
          document.body.style.width = "";
          window.scrollTo(0, scrollYPosition);
          setScrollYPosition(0);
        }, unlockDelay);

        return () => clearTimeout(timer);
      }
    }
  }, [isOpen, scrollYPosition, isMobileOrTablet]);

  useEffect(() => {
    if (!isOpen && pendingScrollId) {
      const scrollExecutionDelay = isMobileOrTablet() ? 700 : 0;

      const timer = setTimeout(() => {
        if (pendingScrollId === "home") {
          if (!isMobileOrTablet()) {
            // If on desktop
            if (mainContentRef.current) {
              mainContentRef.current.scrollTo({ top: 0, behavior: "smooth" }); // Scroll main-content to top
            }
          } else {
            // If on mobile/tablet
            window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll the whole window to top
          }
        } else {
          // For other sections (Skills, Projects, Blogs)
          const section = document.getElementById(pendingScrollId);
          if (section) {
            if (isMobileOrTablet()) {
              const targetTop =
                section.getBoundingClientRect().top + window.scrollY;
              window.scrollTo({ top: targetTop, behavior: "smooth" });
            } else if (mainContentRef.current) {
              // For desktop, scroll the main-content div for other sections
              const targetScrollPosition =
                section.getBoundingClientRect().top +
                mainContentRef.current.scrollTop;
              mainContentRef.current.scrollTo({
                top: targetScrollPosition,
                behavior: "smooth",
              });
            }
          } else {
            console.warn(`Section with ID '${pendingScrollId}' not found!`);
          }
        }
        setPendingScrollId(null);
      }, scrollExecutionDelay);

      return () => clearTimeout(timer);
    }
  }, [isOpen, pendingScrollId, mainContentRef, isMobileOrTablet]);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const toggleDarkMode = () => setIsDarkMode(!isDarkMode);

  const handleNavLinkClick = (e, id) => {
    e.preventDefault();
    setPendingScrollId(id);
    setIsOpen(false);
  };

  return (
    <>
      {/* Hamburger Toggle */}
      <button
        className={`menu-toggle ${isOpen ? "active" : ""}`}
        onClick={toggleSidebar}
        aria-label="Toggle navigation"
      >
        <i className={`fas ${isOpen ? "fa-times" : "fa-bars"}`}></i>
      </button>

      {/* Sidebar */}
      <div className={`sidebar-wrapper ${isOpen ? "active" : ""}`}>
        <aside className="sidebar">
          {/* Header */}
          <div className="sidebar-header">
            <img
              src={profileImg}
              alt="Sailesh Kafle's Profile"
              className="profile-photo"
            />
            <h1 className="name">Sailesh Kafle</h1>
            <p className="role">Software Developer . AI Engineer</p>

            <div className="sidebar-social-links">
              <a
                href="https://github.com/Sailesh75"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="GitHub Profile"
              >
                <GitHubIcon className="social-icon github-icon" />
              </a>
              <a
                href="https://www.linkedin.com/in/saileshkafle/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn Profile"
              >
                <LinkedInIcon className="social-icon linkedin-icon" />
              </a>
            </div>
          </div>

          {/* Scrollable Navigation Area */}
          <div className="sidebar-scroll">
            <nav className="sidebar-nav">
              <ul>
                <li>
                  <a
                    href="#home"
                    className={activeSection === "home" ? "active" : ""}
                    onClick={(e) => handleNavLinkClick(e, "home")}
                  >
                    <i className="fas fa-home"></i> Home
                  </a>
                </li>
                <li>
                  <a
                    href="#skills"
                    className={activeSection === "skills" ? "active" : ""}
                    onClick={(e) => handleNavLinkClick(e, "skills")}
                  >
                    <i className="fas fa-code"></i> Skills
                  </a>
                </li>
                <li>
                  <a
                    href="#projects"
                    className={activeSection === "projects" ? "active" : ""}
                    onClick={(e) => handleNavLinkClick(e, "projects")}
                  >
                    <i className="fas fa-project-diagram"></i> Projects
                  </a>
                </li>

                <li>
                  <a
                    href="#blog-publications"
                    className={
                      activeSection === "blog-publications" ? "active" : ""
                    }
                    onClick={(e) => handleNavLinkClick(e, "blog-publications")}
                  >
                    <i className="fas fa-newspaper"></i> Publications & Certification
                  </a>
                </li>
                <li>
                  <a href={resumePdf} target="_blank" rel="noopener noreferrer">
                    <i className="fas fa-file-alt"></i> Resume
                  </a>
                </li>
                <li>
                  <a href="#darkmode" onClick={toggleDarkMode}>
                    <i
                      className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}
                    ></i>{" "}
                    {isDarkMode ? "Light Mode" : "Dark Mode"}
                  </a>
                </li>
              </ul>
            </nav>
          </div>
        </aside>
      </div>
    </>
  );
};

export default Sidebar;
